import Masonry from "@mui/lab/Masonry";
import Box from "@mui/material/Box";
import { Layout } from "../../components/Layout";
import { MsgProvider } from "../../components/Msg";
import { useMsg } from "../../components/Msg/Msg";
import { H2, P } from "../../components/Typography";
import { parametrizedRoutes } from "../../routes/constants";
import { useAuth } from "../Authorization";
import { useUpcomingCoachSessionsQuery } from "../Clients/api";
import { useUserUpcomingSessionsQuery } from "../Coaches/api";
import { Loaders, QueryRenderer } from "../QM/QueryRenderer";
import { AiHelperCard, AiHelperPlaceholders } from "./AiHelper";
import { AreaAndLongTermGoal } from "./AreaAndLongTermGoal";
import { DashboardBlockTitle } from "./DashboardBlockTitle";
import { DashboardHeader } from "./DashboardHeader";
import {
  anyTruthy,
  DashboardRightMenu,
  userArticlesKey,
  userPreviewsKey,
  useUserInsights,
} from "./DashboardRightMenu";
import { RESOURCE_TYPE, ResourceMediaCard } from "./ResourceMediaCard";
import { UpcomingAndActions } from "./UpcomingAndActions";
import { dashboardMessages } from "./messages";

const parseArraySafely = (data) => {
  try {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    return JSON.parse(data);
  } catch (e) {
    console.log(">>> error parsing previews", { e, data });
    return [];
  }
};

export function DashboardPage() {
  const { isCoach, user } = useAuth();
  const dashboardMsg = useMsg({ dict: dashboardMessages });
  const insightsQuery = useUserInsights();
  const coachUpcomingSessionsQuery = useUpcomingCoachSessionsQuery({
    enabled: isCoach,
  });
  const userUpcomingSessionsQuery = useUserUpcomingSessionsQuery({
    enabled: !isCoach,
  });
  const upcomingSessionsQuery = isCoach
    ? coachUpcomingSessionsQuery
    : userUpcomingSessionsQuery;
  const isSessionScheduled = !!upcomingSessionsQuery.data?.length;
  const displayLeadershipJourney =
    !!user.data.areaOfDevelopment[0] || isSessionScheduled;
  console.log({ userUpcomingSessionsQuery });

  return (
    <MsgProvider messages={dashboardMessages}>
      <Layout
        rightMenuContent={<DashboardRightMenu insightsQuery={insightsQuery} />}
        HeaderComponent={DashboardHeader}
        rightMenuOpenSinceBreakpoint="md"
        rightMenuPermanentSinceBreakpoint="md"
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {!!displayLeadershipJourney && (
            <>
              <Box>
                <H2 sx={{ mb: 0.5 }}>
                  {dashboardMsg("dashboard.rightmenu.title")}
                </H2>
                <P>{dashboardMsg("dashboard.perex")}</P>
              </Box>
              <AreaAndLongTermGoal />
              <UpcomingAndActions insightsQuery={insightsQuery} />
            </>
          )}

          <AiHelperCard insightsQuery={insightsQuery} />

          <DashboardBlockTitle
            iconName={"School"}
            isPending={
              !insightsQuery.data ||
              anyTruthy(
                "isPending",
                [userPreviewsKey, userArticlesKey],
                insightsQuery.data || {},
              )
            }
          >
            {dashboardMsg("dashboard.learn.heading")}
          </DashboardBlockTitle>
          <QueryRenderer
            query={insightsQuery}
            loaderEl={<Loaders.CircularBlock sx={{ my: 1.5 }} />}
            errored={null}
            success={({ data }) => {
              const isPending = anyTruthy(
                "isPending",
                [userPreviewsKey, userArticlesKey],
                insightsQuery.data || {},
              );
              const videosMaybeString = data[userPreviewsKey]?.text;
              const videosList = parseArraySafely(videosMaybeString).map(
                (item) => ({
                  ...item,
                  type: RESOURCE_TYPE.VIDEO,
                }),
              );
              const articlesMaybeString = data[userArticlesKey]?.text;
              const articlesList = parseArraySafely(articlesMaybeString).map(
                (item) => ({
                  ...item,
                  type: RESOURCE_TYPE.ARTICLE,
                }),
              );
              const list = [...articlesList, ...videosList];

              console.log("[Insights.rndr]", {
                data,
                articlesList,
                videosList,
                articlesMaybeString,
                videosMaybeString,
              });

              if (!list.length) return <AiHelperPlaceholders />;

              return (
                <>
                  <Masonry
                    columns={{ xs: 1, sm: 2, lg: 3, xl: 4, xxl: 5 }}
                    spacing={2}
                    sx={{
                      opacity: isPending ? 0.5 : 1,
                      transition: "opacity 0.2s ease-in-out",
                    }}
                  >
                    {list.map((item) => {
                      const {
                        title = "",
                        perex = "",
                        url = "",
                        thumbnail,
                        imageData,
                        type,
                        length = "",
                        readTime = "",
                      } = item || {};
                      return (
                        <ResourceMediaCard
                          key={JSON.stringify({ title, url })}
                          title={title}
                          perex={perex}
                          href={
                            // ARTICLE href is link to original untranslated article, VIDEO href is link to video
                            type === RESOURCE_TYPE.VIDEO
                              ? url
                              : parametrizedRoutes.article({
                                  articleId: item.id,
                                  displayPerex:
                                    perex?.length >
                                    ResourceMediaCard.PEREX_MAX_LENGTH
                                      ? 1
                                      : 0,
                                })
                          }
                          externalHref={
                            type === RESOURCE_TYPE.VIDEO ? true : false
                          }
                          thumbnail={thumbnail} // just videos now
                          rawBase64={imageData} // just for articles now
                          type={type}
                          length={
                            type === RESOURCE_TYPE.VIDEO
                              ? length
                              : type === RESOURCE_TYPE.ARTICLE
                                ? readTime
                                : ""
                          }
                        />
                      );
                    })}
                  </Masonry>
                </>
              );
            }}
          />
        </Box>
      </Layout>
    </MsgProvider>
  );
}
